import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  Dimensions,
  Animated,
  StatusBar,
  TouchableOpacity,
  Alert,
  Platform,
  BackHandler,
} from 'react-native';

interface SplashScreenProps {
  onFinish: () => void;
}

const { width, height } = Dimensions.get('window');

const SplashScreen: React.FC<SplashScreenProps> = ({ onFinish }) => {
  const [fadeAnim] = useState(new Animated.Value(0));
  const [scaleAnim] = useState(new Animated.Value(0.8));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Background images representing different booking scenarios
  const backgroundImages = [
    {
      id: 1,
      title: "Hair Salon Booking",
      description: "Book your perfect haircut and styling appointment",
      emoji: "💇‍♀️",
      color: "#FF6B9D"
    },
    {
      id: 2,
      title: "Automotive Services",
      description: "Schedule oil changes and car maintenance",
      emoji: "🚗",
      color: "#4ECDC4"
    },
    {
      id: 3,
      title: "Restaurant Reservations",
      description: "Reserve your table at the best restaurants",
      emoji: "🍽️",
      color: "#45B7D1"
    },
    {
      id: 4,
      title: "Medical Appointments",
      description: "Book your healthcare appointments easily",
      emoji: "🏥",
      color: "#96CEB4"
    },
    {
      id: 5,
      title: "Beauty Services",
      description: "Schedule spa treatments and beauty services",
      emoji: "✨",
      color: "#FECA57"
    }
  ];

  useEffect(() => {
    // Start the animation sequence
    startAnimation();
    
    // Cycle through background images every 800ms
    const imageInterval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % backgroundImages.length);
    }, 800);

    // Auto-finish after 4 seconds
    const timer = setTimeout(() => {
      clearInterval(imageInterval);
      onFinish();
    }, 4000);

    return () => {
      clearInterval(imageInterval);
      clearTimeout(timer);
    };
  }, [onFinish]);

  const startAnimation = () => {
    // Fade in animation
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 1000,
      useNativeDriver: true,
    }).start();

    // Scale animation
    Animated.spring(scaleAnim, {
      toValue: 1,
      tension: 50,
      friction: 7,
      useNativeDriver: true,
    }).start();
  };

  const handleCloseApp = () => {
    Alert.alert(
      'Exit BookAPT',
      'Are you sure you want to exit the app?',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Exit',
          style: 'destructive',
          onPress: () => {
            // Close the app
            if (Platform.OS === 'android') {
              BackHandler.exitApp();
            } else {
              // For iOS, we can't programmatically close the app
              // The user will need to use the home button or app switcher
              Alert.alert(
                'App Closed',
                'Thank you for using BookAPT!',
                [{ text: 'OK', onPress: () => {} }]
              );
            }
          },
        },
      ]
    );
  };

  const currentImage = backgroundImages[currentImageIndex];

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#2c3e50" />
      
      {/* Animated Background */}
      <Animated.View 
        style={[
          styles.backgroundContainer,
          {
            backgroundColor: currentImage.color,
            opacity: fadeAnim,
          }
        ]}
      >
        {/* Gradient Overlay */}
        <View style={styles.gradientOverlay} />
        
        {/* Background Pattern */}
        <View style={styles.patternContainer}>
          <View style={styles.patternCircle1} />
          <View style={styles.patternCircle2} />
          <View style={styles.patternCircle3} />
        </View>
      </Animated.View>

      {/* Close Button */}
      <TouchableOpacity
        style={styles.closeButton}
        onPress={handleCloseApp}
        activeOpacity={0.7}
      >
        <Text style={styles.closeButtonText}>✕</Text>
      </TouchableOpacity>

      {/* Content */}
      <Animated.View 
        style={[
          styles.contentContainer,
          {
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }],
          }
        ]}
      >
        {/* App Logo/Icon */}
        <View style={styles.logoContainer}>
          <Text style={styles.logoEmoji}>💬</Text>
          <Text style={styles.appName}>BookAPT</Text>
          <Text style={styles.tagline}>One App for all your appointments</Text>
        </View>

        {/* Current Booking Scenario */}
        <View style={styles.scenarioContainer}>
          <Text style={styles.scenarioEmoji}>{currentImage.emoji}</Text>
          <Text style={styles.scenarioTitle}>{currentImage.title}</Text>
          <Text style={styles.scenarioDescription}>{currentImage.description}</Text>
        </View>

        {/* Loading Indicator */}
        <View style={styles.loadingContainer}>
          <View style={styles.loadingDots}>
            <View style={[styles.dot, styles.dot1]} />
            <View style={[styles.dot, styles.dot2]} />
            <View style={[styles.dot, styles.dot3]} />
          </View>
          <Text style={styles.loadingText}>Loading your booking experience...</Text>
          
          {/* Skip Button */}
          <TouchableOpacity
            style={styles.skipButton}
            onPress={onFinish}
            activeOpacity={0.7}
          >
            <Text style={styles.skipButtonText}>Skip</Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#2c3e50',
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  patternContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  patternCircle1: {
    position: 'absolute',
    top: 100,
    right: -50,
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  patternCircle2: {
    position: 'absolute',
    bottom: 150,
    left: -80,
    width: 300,
    height: 300,
    borderRadius: 150,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  patternCircle3: {
    position: 'absolute',
    top: 300,
    left: 50,
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 60,
  },
  logoEmoji: {
    fontSize: 80,
    marginBottom: 20,
  },
  appName: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 8,
    textShadowColor: 'rgba(0,0,0,0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  tagline: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '300',
    textAlign: 'center',
  },
  scenarioContainer: {
    alignItems: 'center',
    marginBottom: 80,
    backgroundColor: 'rgba(255,255,255,0.15)',
    paddingVertical: 30,
    paddingHorizontal: 40,
    borderRadius: 20,
    backdropFilter: 'blur(10px)',
  },
  scenarioEmoji: {
    fontSize: 50,
    marginBottom: 15,
  },
  scenarioTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 8,
    textAlign: 'center',
  },
  scenarioDescription: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    textAlign: 'center',
    lineHeight: 20,
  },
  loadingContainer: {
    alignItems: 'center',
  },
  loadingDots: {
    flexDirection: 'row',
    marginBottom: 15,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#fff',
    marginHorizontal: 4,
  },
  dot1: {
    opacity: 0.4,
  },
  dot2: {
    opacity: 0.7,
  },
  dot3: {
    opacity: 1,
  },
  loadingText: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '300',
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  closeButtonText: {
    fontSize: 18,
    color: '#fff',
    fontWeight: 'bold',
  },
  skipButton: {
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  skipButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '500',
    textAlign: 'center',
  },
});

export default SplashScreen;